import { Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { getFormattedCategory, translateCategory } from '@mwe/utils';
import { LANGUAGES } from './language.constants';
import { EnvironmentService } from '../environment/environment.service';

@Injectable({ providedIn: 'root' })
export class LanguageHelperService {
  private translateService = inject(TranslateService);
  private titleService = inject(Title);
  private router = inject(Router);
  private envService = inject(EnvironmentService);

  renderer: Renderer2 = null;
  private _language: BehaviorSubject<string>;

  constructor() {
    const rootRenderer = inject(RendererFactory2);

    this._language = new BehaviorSubject<string>(this.translateService.currentLang);
    this.renderer = rootRenderer.createRenderer(document.querySelector('html'), null);
    this.init();
  }

  getAll(): Promise<any> {
    return Promise.resolve(LANGUAGES);
  }

  get language(): Observable<string> {
    return this._language.asObservable();
  }

  /**
   * Update the window title using params in the following
   * order:
   * 1. titleKey parameter
   * 2. $state.$current.data.pageTitle (current state page title)
   * 3. 'global.title'
   */
  updateTitle(titleKeys?: string[], customTitle = ''): void {
    if (!titleKeys) {
      return;
    }

    this.updateTitleFromKeys(titleKeys, customTitle);
  }

  async updateTitleAndAddTranslatedCategory(titleKeys: string[], category: string): Promise<void> {
    const translatedCategory = await translateCategory(category, this.translateService);
    const formatedCategory = getFormattedCategory(translatedCategory);

    this.updateTitleFromKeys(titleKeys, '', formatedCategory);
  }

  updateTitleFromKeys(titleKeys?: string[], customTitle = '', sparte = ''): void {
    if (!titleKeys) {
      return;
    }

    const env = this.envService.getEnvironment();

    const postfix =
      env.customerApp == 'privat' ? this.translateService.instant('global.title') : this.translateService.instant('global.title_business');

    this.translateService.get(titleKeys, { sparte: sparte }).subscribe(translationObject => {
      const titles = Object.values(translationObject);

      if (customTitle) {
        titles.push(customTitle);
      }

      titles.push(postfix);

      const newTitle = titles.join(' - ');

      this.titleService.setTitle(newTitle);
    });
  }

  private init(): void {
    this.translateService.onLangChange?.subscribe((event: LangChangeEvent) => {
      this._language.next(this.translateService.currentLang);
      this.renderer.setAttribute(document.querySelector('html'), 'lang', this.translateService.currentLang);
      this.updateTitle();
    });
  }
}
